import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const ReportPreview = ({ report, onClose, onExport }) => {
  const assetData = [
    { name: 'Equity', value: 45, color: '#1E40AF' },
    { name: 'Real Estate', value: 25, color: '#059669' },
    { name: 'Fixed Deposits', value: 15, color: '#F59E0B' },
    { name: 'Gold', value: 10, color: '#EF4444' },
    { name: 'Cash', value: 5, color: '#64748B' }
  ];

  const projectionData = [
    { year: '2024', netWorth: 850000, target: 800000 },
    { year: '2025', netWorth: 920000, target: 900000 },
    { year: '2026', netWorth: 1050000, target: 1000000 },
    { year: '2027', netWorth: 1180000, target: 1150000 },
    { year: '2028', netWorth: 1320000, target: 1300000 },
    { year: '2029', netWorth: 1480000, target: 1500000 }
  ];

  const incomeExpenseData = [
    { month: 'Jan', income: 12000, expenses: 8500 },
    { month: 'Feb', income: 12000, expenses: 9200 },
    { month: 'Mar', income: 12500, expenses: 8800 },
    { month: 'Apr', income: 12000, expenses: 9500 },
    { month: 'May', income: 12000, expenses: 8700 },
    { month: 'Jun', income: 13000, expenses: 9100 }
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <Icon name={report.icon} size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-semibold text-foreground">{report.title}</h2>
              <p className="text-sm text-muted-foreground">Preview - {report.dateRange}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              onClick={() => onExport(report)}
              iconName="Download"
              iconPosition="left"
            >
              Export
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
            >
              <Icon name="X" size={20} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Executive Summary */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                Executive Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-foreground">₹1,320,000</div>
                  <div className="text-sm text-muted-foreground">Current Net Worth</div>
                  <div className="text-xs text-success mt-1">↗ +12.5% YoY</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-foreground">87%</div>
                  <div className="text-sm text-muted-foreground">Retirement Readiness</div>
                  <div className="text-xs text-success mt-1">↗ +5% this quarter</div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="text-2xl font-bold text-foreground">₹42,000</div>
                  <div className="text-sm text-muted-foreground">Monthly Surplus</div>
                  <div className="text-xs text-success mt-1">↗ +8% vs target</div>
                </div>
              </div>
            </section>

            {/* Asset Allocation */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                Asset Allocation Analysis
              </h3>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-muted/50 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-4">Current Portfolio Distribution</h4>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={assetData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          dataKey="value"
                        >
                          {assetData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => `${value}%`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="space-y-3">
                  <h4 className="font-medium text-foreground">Asset Breakdown</h4>
                  {assetData.map((asset, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-background rounded-lg">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded-full" 
                          style={{ backgroundColor: asset.color }}
                        ></div>
                        <span className="text-foreground">{asset.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-foreground">{asset.value}%</div>
                        <div className="text-xs text-muted-foreground">
                          ₹{(1320000 * asset.value / 100).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Net Worth Projection */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                Net Worth Projection
              </h3>
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={projectionData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="year" stroke="var(--color-muted-foreground)" />
                      <YAxis 
                        stroke="var(--color-muted-foreground)"
                        tickFormatter={(value) => `₹${(value / 1000)}K`}
                      />
                      <Tooltip 
                        formatter={(value) => [`₹${value.toLocaleString()}`, '']}
                        labelStyle={{ color: 'var(--color-foreground)' }}
                        contentStyle={{ 
                          backgroundColor: 'var(--color-card)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px'
                        }}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="netWorth" 
                        stroke="var(--color-primary)" 
                        strokeWidth={3}
                        name="Projected Net Worth"
                      />
                      <Line 
                        type="monotone" 
                        dataKey="target" 
                        stroke="var(--color-accent)" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Target"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>

            {/* Income vs Expenses */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                Income vs Expenses Trend
              </h3>
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={incomeExpenseData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
                      <YAxis 
                        stroke="var(--color-muted-foreground)"
                        tickFormatter={(value) => `$${(value / 1000)}K`}
                      />
                      <Tooltip 
                        formatter={(value) => [`$${value.toLocaleString()}`, '']}
                        labelStyle={{ color: 'var(--color-foreground)' }}
                        contentStyle={{ 
                          backgroundColor: 'var(--color-card)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar dataKey="income" fill="var(--color-accent)" name="Income" />
                      <Bar dataKey="expenses" fill="var(--color-warning)" name="Expenses" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>

            {/* Key Recommendations */}
            <section className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground border-b border-border pb-2">
                Key Recommendations
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/20 rounded-lg">
                  <Icon name="CheckCircle" size={20} className="text-success mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground">Excellent Progress</h4>
                    <p className="text-sm text-muted-foreground">
                      Your retirement savings are on track. Current trajectory shows 87% readiness for your target retirement age.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-warning/10 border border-warning/20 rounded-lg">
                  <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground">Consider Diversification</h4>
                    <p className="text-sm text-muted-foreground">
                      Your equity allocation at 45% is appropriate, but consider increasing international exposure for better diversification.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-4 bg-primary/10 border border-primary/20 rounded-lg">
                  <Icon name="TrendingUp" size={20} className="text-primary mt-0.5" />
                  <div>
                    <h4 className="font-medium text-foreground">Optimize Tax Strategy</h4>
                    <p className="text-sm text-muted-foreground">
                      Review tax-advantaged account contributions. You have room to increase 401(k) contributions by ₹8,000 annually.
                    </p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportPreview;